import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  EXCLUSIVE_RESOURCE_TYPES,
  fetchConcurrencyStats,
  listResourceLocks,
  releaseResource,
  reserveResource,
  resourceTypeMeta,
  type CoreResourceLock,
} from '@/lib/coreConcurrency';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ConcurrencyTab({ org }: Props) {
  const [locks, setLocks] = useState<CoreResourceLock[]>([]);
  const [stats, setStats] = useState({ activeLocks: 0, releasedLocks: 0, byResourceType: {} as Record<string, number> });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [resourceType, setResourceType] = useState('room');
  const [resourceId, setResourceId] = useState('101');
  const [startAt, setStartAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [endAt, setEndAt] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 16));
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [l, s] = await Promise.all([listResourceLocks(org.id), fetchConcurrencyStats(org.id)]);
      setLocks(l);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const reserve = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await reserveResource({
        tenantId: org.tenant_id,
        organizationId: org.id,
        resourceType,
        resourceId,
        label: `${resourceTypeMeta(resourceType).label} ${resourceId}`,
        holderName: 'Test dashboard',
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
      });
      if (res.ok) {
        setFeedback({ ok: true, text: `Ressource réservée (verrou #${res.lockId}).` });
      } else {
        setFeedback({
          ok: false,
          text: `Conflit détecté : « ${resourceTypeMeta(resourceType).label} ${resourceId} » est déjà prise sur cette plage (verrou #${res.conflict?.id}, tenu par ${res.conflict?.holder_name || '—'}).`,
        });
      }
      load();
    } catch (e) {
      setFeedback({ ok: false, text: e instanceof Error ? e.message : 'Erreur.' });
    } finally {
      setBusy(false);
    }
  };

  const release = async (id: number) => {
    try { await releaseResource(id); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Concurrence / double booking</span> : deux utilisateurs ne doivent jamais réserver
        simultanément la même ressource exclusive (rendez-vous, chambre, appartement, villa, équipement…). La vérification de conflit est faite
        <span className="font-semibold text-foreground-700"> au moment de l’action</span>, avant de poser le verrou.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-lock-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.activeLocks}</p>
          <p className="text-xs text-foreground-500">Verrous actifs</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-lock-unlock-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.releasedLocks}</p>
          <p className="text-xs text-foreground-500">Libérés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-box-3-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{Object.keys(stats.byResourceType).length}</p>
          <p className="text-xs text-foreground-500">Types de ressources</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-shield-check-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">Serveur</p>
          <p className="text-xs text-foreground-500">Contrôle au moment de l’action</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-calendar-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Tester une réservation de ressource</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type de ressource</span>
              <select className={INPUT} value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
                {EXCLUSIVE_RESOURCE_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant de ressource</span>
              <input className={INPUT} value={resourceId} onChange={(e) => setResourceId(e.target.value)} placeholder="Ex : 101" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Début</span>
              <input type="datetime-local" className={INPUT} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Fin</span>
              <input type="datetime-local" className={INPUT} value={endAt} onChange={(e) => setEndAt(e.target.value)} />
            </label>
          </div>
          <button onClick={reserve} disabled={busy} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-lock-2-line"></i>} Réserver
          </button>
          {feedback && (
            <div className={`rounded-md border p-3 text-xs ${feedback.ok ? 'bg-primary-50 border-primary-200 text-primary-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <i className={feedback.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i> {feedback.text}
            </div>
          )}
          <p className="text-[11px] text-foreground-400">Astuce : réservez deux fois la même ressource sur des plages qui se chevauchent — le second essai est refusé.</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-list-check"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Verrous de ressources</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : locks.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun verrou. Réservez une ressource ci-dessus.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[380px] overflow-y-auto">
            {locks.map((l) => (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0"><i className={resourceTypeMeta(l.resource_type).icon}></i></span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground-800 truncate">{l.label || `${resourceTypeMeta(l.resource_type).label} ${l.resource_id}`}</p>
                  <p className="text-[11px] text-foreground-400 truncate">
                    {l.start_at ? new Date(l.start_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    {' → '}
                    {l.end_at ? new Date(l.end_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    {l.holder_name ? ` · ${l.holder_name}` : ''}
                  </p>
                </div>
                {l.status === 'active' ? (
                  <button onClick={() => release(l.id)} className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
                    Libérer
                  </button>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">libéré</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}